/**
 * Section-header content reveals — SplitText headline lines, optional aside
 * fade, then body (pin + prose) fade. See docs/04-motion.md.
 *
 * - `introHeaderReveal` — 01 Introduction (scrubbed on the hero runway)
 * - `sectionHeaderReveal` — 02 State / 03 Pressure / 04 Response (scrubbed
 *   on each section's own ScrollTrigger window)
 *
 * Swirl travel stays in `heroScene` (`src/motion/tokens.ts`).
 */

/**
 * 01 Introduction Flyways — tuned against the hero runway while
 * `#introduction` is pinned. Scrub timelines treat `weight` as relative
 * segment length. Always boolean `scrub: true` (never numeric lag on touch).
 */
export const introHeaderReveal = {
  /**
   * ScrollTrigger `start` / `end` for the full content timeline on the runway.
   * Mid-travel while the live swirl clip reveals `#introduction` so chrome /
   * SplitText / body fade inside the cream circle.
   */
  timelineStart: 'top+=25% top',
  timelineEnd: 'top+=52% top',
  chrome: {
    /** Segment weight within the content timeline. */
    weight: 1,
    /** Rise distance (px) for `[data-hero-reveal]` chrome. */
    offsetY: 24,
    ease: 'none',
  },
  /** Arctic Tern aside and nested stat reveal with the headline segment. */
  aside: {
    offsetY: 24,
    /** Nested stat start scale — the surrounding aside does not scale. */
    fromScale: 0.85,
    ease: 'none',
  },
  /**
   * Headline units: SplitText characters for Latin locales and intact words
   * for Arabic, with `mask: "lines"`. Introduction + Flyways stagger in parallel.
   */
  headline: {
    weight: 2.5,
    yPercent: 45,
    rotationDeg: 11,
    transformOriginLtr: '0% 50%',
    transformOriginRtl: '100% 50%',
    stagger: 0.06,
    staggerOffsetYPx: 10,
    ease: 'none',
    mask: 'lines' as const,
  },
  /**
   * Body (`[data-section-body-reveal]`) — scrubbed bidirectional fade.
   * LocationPin / SectionProseReveal Motion still play once discernible.
   */
  body: {
    weight: 1.2,
    offsetY: 24,
    ease: 'none',
    earlyByHeadlineFraction: 0.8,
    duration: 0.5,
  },
} as const;

/**
 * Shared content timing for 02 State / 03 Pressure / 04 Response.
 * Scrubbed and bidirectional on each section's own ScrollTrigger window.
 * `weight` is relative segment length within that scrub range (faster proportions
 * than intro so the sequence finishes in a shorter scroll distance).
 */
export const sectionHeaderReveal = {
  /**
   * ScrollTrigger window — header top crosses these viewport lines while the
   * timeline scrubs. Bidirectional: scrolling back reverses letters / body.
   */
  timelineStart: 'top 85%',
  timelineEnd: 'top 40%',
  aside: {
    offsetY: 24,
    /** Nested stat start scale — the surrounding aside does not scale. */
    fromScale: 0.85,
    ease: 'none',
  },
  headline: {
    weight: 1.15,
    yPercent: 45,
    rotationDeg: 11,
    transformOriginLtr: '0% 50%',
    transformOriginRtl: '100% 50%',
    stagger: 0.03,
    staggerOffsetYPx: 6,
    ease: 'none',
    mask: 'lines' as const,
  },
  body: {
    weight: 0.55,
    offsetY: 20,
    ease: 'none',
    earlyByHeadlineFraction: 0.75,
    duration: 0.3,
  },
} as const;

/** Content knobs passed into the shared GSAP builder (intro or chapter). */
export type SectionHeaderContentTiming = {
  aside: {
    offsetY: number;
    fromScale: number;
    ease: string;
  };
  headline: {
    weight: number;
    yPercent: number;
    rotationDeg: number;
    transformOriginLtr: string;
    transformOriginRtl: string;
    stagger: number;
    staggerOffsetYPx: number;
    ease: string;
    mask: 'lines';
  };
  body: {
    weight: number;
    offsetY: number;
    ease: string;
    earlyByHeadlineFraction: number;
    duration: number;
  };
};
